import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import { useSelector } from "react-redux";
import { useNotification } from "../../../../hooks/useNotification";

export default function CopyShopDataModal({ isOpen, setIsOpen, onSuccess }) {
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [sourceShopId, setSourceShopId] = useState("");
  const [targetShopId, setTargetShopId] = useState("");
  const [isCopyTag, setIsCopyTag] = useState(false);
  const [isCopyQuickMessage, setIsCopyQuickMessage] = useState(false);
  const [isOverwriteQuickMessage, setIsOverwriteQuickMessage] = useState(false);

  const staff = useSelector((state) => state.auth.user);
  const accountId = useSelector((state) => state.auth.user.accountId);

  useEffect(() => {
    if (isOpen) {
      fetchShops();
      // Reset form
      setSourceShopId("");
      setTargetShopId("");
      setIsCopyTag(false);
      setIsCopyQuickMessage(false);
      setIsOverwriteQuickMessage(false);
    }
  }, [isOpen]);

  const fetchShops = async () => {
    try {
      const res = await shopApi.getShops({
        accountId,
        staffId: staff.id,
      });

      if (res.length > 0) {
        setShops(res);
      }
    } catch (error) {
      console.error("Lỗi lấy danh sách shop:", error);
    }
  };

  const handleSave = async () => {
    if (!sourceShopId || !targetShopId) {
      addNotification("Vui lòng chọn Zalo nguồn và Zalo đích", -1);
      return;
    }

    if (sourceShopId === targetShopId) {
      addNotification("Zalo nguồn và đích không thể giống nhau", -1);
      return;
    }

    if (!isCopyTag && !isCopyQuickMessage) {
      addNotification("Vui lòng chọn ít nhất một nội dung để sao chép", -1);
      return;
    }

    try {
      setLoading(true);
      const res = await shopApi.copyData({
        sourceShopId,
        targetShopId,
        isCopyTag,
        isCopyQuickMessage,
        isOverwriteQuickMessage,
      });

      if (res.status === 1) {
        addNotification("Sao chép thành công", 1);
        setIsOpen(false);
        if (onSuccess) onSuccess();
      } else {
        addNotification(res.message, -1);
      }
    } catch (error) {
      addNotification(error.message, -1);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Sao chép tin nhắn nhanh/tag</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-[100px_1fr] gap-4 items-center">
            <label className="font-medium text-gray-700">Từ Zalo</label>
            <select
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={sourceShopId}
              onChange={(e) => setSourceShopId(e.target.value)}
            >
              <option value="">Chọn Zalo</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-[100px_1fr] gap-4 items-center">
            <label className="font-medium text-gray-700">Sang Zalo</label>
            <select
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={targetShopId}
              onChange={(e) => setTargetShopId(e.target.value)}
            >
              <option value="">Chọn Zalo</option>
              {shops
                .filter((s) => s.id !== sourceShopId)
                .map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="space-y-3 pl-[116px]">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                checked={isCopyTag}
                onChange={(e) => setIsCopyTag(e.target.checked)}
              />
              <span className="text-gray-700 group-hover:text-gray-900">
                Tag
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                checked={isCopyQuickMessage}
                onChange={(e) => setIsCopyQuickMessage(e.target.checked)}
              />
              <span className="text-gray-700 group-hover:text-gray-900">
                Tin nhắn nhanh
              </span>
            </label>

            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  checked={isOverwriteQuickMessage}
                  onChange={(e) => setIsOverwriteQuickMessage(e.target.checked)}
                  disabled={!isCopyQuickMessage}
                />
                <span
                  className={`text-sm ${!isCopyQuickMessage ? "text-gray-400" : "text-gray-600 group-hover:text-gray-800"}`}
                >
                  Xoá tin nhắn nhanh cũ &gt; thêm mới
                  <br />
                  (Mặc định là add thêm mới, giữ nguyên cái cũ)
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
}
